import {
  type Type,
  matchType,
  getSystem,
  getMeta,
  Plugins,
  Types,
} from '@packages/systems/wfdl';
import {
  getName,
  getPluginName,
  qualify,
  typeName,
  namesEqual,
} from '@packages/systems/wfdl-Qualified';
import template from '@babel/template';
import generate from '@babel/generator';
import * as t from '@babel/types';
import {Json, matchJson} from '@packages/utilities/json';

import {TTypeApplication} from '@packages/systems/wfdl-ast';

const TypeApplicationTemplate = template.expression(`
  TTypeApplication({
    con: %%con%%,
    arg: %%arg%%,
  })
`);

const RowConsTemplate = template.expression(`
  TRowCons({
    label: %%label%%,
    type: %%type%%,
    tail: %%tail%%,
  })
`);

const TypeConstructorTemplate = template.expression(`
  TTypeConstructor(qualify(Plugins.name(%%plugin%%), typeName(%%type%%)))
`);

const GenericTemplate = template.expression(`
  TGeneric({
    arg: %%arg%%,
    body: %%body%%,
  })
`);

const TypeReferenceTemplate = template.expression(`
  TTypeReference(%%name%%)
`);

const RecordTemplate = template.expression(`
  TRecord(%%fields%%)
`);

const RecordWithTailTemplate = template.expression(`
  TRecord(%%fields%%, %%tail%%)
`);

const EnumTemplate = template.expression(`
  TEnum(%%values%%)
`);

const UnionTemplate = template.expression(`
  TUnion(%%values%%)
`);

const UnionWithTailTemplate = template.expression(`
  TUnion(%%values%%, %%tail%%)
`);

const ListTemplate = template.expression(`
  TList(%%itemType%%)
`);

const ElementOfTemplate = template.expression(`
  TElementOf(%%context%%, %%state%%)
`);

const functionTypeName = qualify(Plugins.name('Builtin'), typeName('Function'));
const functionOfTemplate = template.expression(`
  functionOf(
    %%arg%%,
    %%ret%%
  )
`);

const withSystemTemplate = template.expression(`
  withSystem(%%system%%, %%type%%)
`);

const withMetaTemplate = template.expression(`
  withMeta(%%meta%%, %%type%%)
`);

const textTypeName = qualify(Plugins.name('Builtin'), typeName('Text'));
const TextTemplate = template.expression(`TText`);

const booleanTypeName = qualify(Plugins.name('Builtin'), typeName('Boolean'));
const BooleanTemplate = template.expression(`TBoolean`);

const numberTypeName = qualify(Plugins.name('Builtin'), typeName('Number'));
const NumberTemplate = template.expression(`TNumber`);

const jsonToASTNode: (json: Json) => t.Expression = matchJson<t.Expression>({
  Array: (arr) => t.arrayExpression(arr.map(jsonToASTNode)),
  Object: (obj) =>
    t.objectExpression(
      Object.keys(obj).map((k) => {
        const value = obj[k];
        return t.objectProperty(
          t.stringLiteral(k),
          value === undefined ? t.identifier('undefined') : jsonToASTNode(value)
        );
      })
    ),
  Boolean: (a) => t.booleanLiteral(a),
  Number: (a) => t.numericLiteral(a),
  String: (a) => t.stringLiteral(a),
  Null: (_) => t.nullLiteral(),
});

const getFunctionParams: (arg1: Type) => null | {
  arg: Type;
  ret: Type;
} = matchType({
  _: (_) => null,
  TypeApplication: ({arg, con}) =>
    matchType({
      _: (_) => null,
      TypeApplication: (conType) =>
        matchType({
          _: (_) => null,
          TypeConstructor: (subConTypeConstructor) =>
            namesEqual(subConTypeConstructor, functionTypeName)
              ? {
                  arg: conType.arg,
                  ret: arg,
                }
              : null,
        })(conType.con),
    })(con),
});

export const typeToAST = (type: Type): t.Expression => {
  let typeASTNode = toAST_(type);
  const system = getSystem(type);
  if (system) {
    typeASTNode = withSystemTemplate({
      system: jsonToASTNode(system as Json),
      type: typeASTNode,
    });
  }

  const meta = getMeta(type);
  if (meta) {
    typeASTNode = withMetaTemplate({
      meta: jsonToASTNode(meta),
      type: typeASTNode,
    });
  }

  return typeASTNode;
};

const toAST_ = matchType({
  Element: () => t.identifier('TElement'),
  Generic: (generic) =>
    GenericTemplate({
      arg: t.stringLiteral(generic.arg),
      body: typeToAST(generic.body),
    }),
  RowCons: (rowCons) =>
    RowConsTemplate({
      label: t.stringLiteral(rowCons.label),
      type: typeToAST(rowCons.type),
      tail: typeToAST(rowCons.tail),
    }),
  RowEmpty: () => t.identifier('TRowEmpty'),
  TypeApplication: (tyApp) => {
    const type = TTypeApplication(tyApp);
    const tail = Types.deconstructRow(tyApp.arg).tail;

    // If `fields` is non-null we're working with a `Record`:
    const fields = Types.getRecordFields(type);
    if (fields) {
      const fieldsAST = t.objectExpression(
        Object.keys(fields).map((fieldName) =>
          t.objectProperty(
            t.stringLiteral(fieldName),
            // @ts-expect-error - TS2345 - Argument of type 'Type | undefined' is not assignable to parameter of type 'Type'.
            typeToAST(fields[fieldName])
          )
        )
      );

      if (Types.isRowEmpty(tail)) {
        return RecordTemplate({
          fields: fieldsAST,
        });
      } else {
        return RecordWithTailTemplate({
          fields: fieldsAST,
          tail: typeToAST(tail),
        });
      }
    }

    const enumValues = Types.getEnumValues(type);
    if (enumValues) {
      return EnumTemplate({
        values: t.objectExpression(
          Object.keys(enumValues).map((enumName) =>
            t.objectProperty(
              t.stringLiteral(enumName),
              // @ts-expect-error - TS2345 - Argument of type 'string | undefined' is not assignable to parameter of type 'string'.
              t.stringLiteral(enumValues[enumName])
            )
          )
        ),
      });
    }

    const unionValues = Types.getUnionValues(type);
    if (unionValues) {
      const unionValuesAST = t.objectExpression(
        Object.keys(unionValues).map((unionKey) =>
          t.objectProperty(
            t.stringLiteral(unionKey),
            // @ts-expect-error - TS2345 - Argument of type 'Type | undefined' is not assignable to parameter of type 'Type'.
            typeToAST(unionValues[unionKey])
          )
        )
      );

      if (Types.isRowEmpty(tail)) {
        return UnionTemplate({
          values: unionValuesAST,
        });
      } else {
        return UnionWithTailTemplate({
          values: unionValuesAST,
          tail: typeToAST(tail),
        });
      }
    }

    const listItemType = Types.getListItemType(type);
    if (listItemType) {
      return ListTemplate({
        itemType: typeToAST(listItemType),
      });
    }

    const elementArgs = Types.getElementTypeArgs(type);
    if (elementArgs) {
      return ElementOfTemplate({
        context: typeToAST(elementArgs.context),
        state: typeToAST(elementArgs.state),
      });
    }

    // FIXME: Should `getFunctionParams` be part of our WFDL API?
    const functionParams = getFunctionParams(type);
    if (functionParams) {
      return functionOfTemplate({
        arg: typeToAST(functionParams.arg),
        ret: typeToAST(functionParams.ret),
      });
    }

    const {con, arg} = tyApp;
    return TypeApplicationTemplate({
      con: typeToAST(con),
      arg: typeToAST(arg),
    });
  },
  TypeConstructor: (tyCon) => {
    if (namesEqual(tyCon, textTypeName)) {
      return TextTemplate();
    }
    if (namesEqual(tyCon, numberTypeName)) {
      return NumberTemplate();
    }
    if (namesEqual(tyCon, booleanTypeName)) {
      return BooleanTemplate();
    }
    return TypeConstructorTemplate({
      plugin: t.stringLiteral(getPluginName(tyCon)),
      type: t.stringLiteral(getName(tyCon)),
    });
  },
  TypeReference: (tyRef) =>
    TypeReferenceTemplate({name: t.stringLiteral(tyRef)}),

  Rigid: () => {
    throw new Error('Rigid types not supported');
  },
  Unknown: () => {
    throw new Error('Unknown types not supported');
  },
});

export function typeToSource(type: Type): string {
  return generate(typeToAST(type)).code;
}
